import { InputType } from 'config'

export interface StepdownEntry {
  percentage: string
  startTerm: number
  endTerm: number
}

export interface PrepaymentPenalty {
  /** Stable id for list keys and reordering; added on save if missing (legacy rows). */
  id?: string
  label: string
  type: 'flat' | 'stepdown' | 'interestBased'
  penaltyFee: string | StepdownEntry[] // For Flat: "5%", For Stepdown: array, For Interest: "6" (months)
  basedOn: 'cupb' | 'orignialLoanAmount'
  maxPrepaymentPercentage: number // Maximum % of UPB that can be prepaid without penalty per year
}

/** Config list from API; order of items is the option order on the loan Penalties screen. */
export const asPrepaymentPenaltyList = (data: unknown): PrepaymentPenalty[] => {
  if (Array.isArray(data)) return data as PrepaymentPenalty[]
  if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>
    if (Array.isArray(o.data)) return o.data as PrepaymentPenalty[]
    if (Array.isArray(o.value)) return o.value as PrepaymentPenalty[]
  }
  return []
}

/** Ensures each penalty has an id before persisting (array order is the display order everywhere). */
export const prepaymentPenaltiesWithIds = (list: PrepaymentPenalty[]): PrepaymentPenalty[] =>
  list.map((p) => ({ ...p, id: p.id ?? crypto.randomUUID() }))

export const getPenaltyTypeOptions = (): Record<string, any> => ({
  flat: 'Flat',
  stepdown: 'Stepdown/Other',
  interestBased: 'Interest Based',
})

export const getBasedOnOptions = (): Record<string, any> => ({
  cupb: 'Current Unpaid Principal Balance',
  orignialLoanAmount: 'Original Loan Amount',
})

export const getBasicInputs = (): Record<string, InputType> => ({
  label: {
    inputType: 'text',
    title: 'Label',
    required: true,
  },
  type: {
    inputType: 'select',
    title: 'Penalty Type',
    options: getPenaltyTypeOptions(),
    required: true,
    hasDefaultOption: true,
  },
  basedOn: {
    inputType: 'select',
    title: 'Based On',
    options: getBasedOnOptions(),
    required: true,
  },
  maxPrepaymentPercentage: {
    inputType: 'text',
    type: 'number',
    title: 'Maximum % of UPB may be prepaid without penalty per year',
    required: true,
    // tooltip: 'If 20% is entered and loan is $100,000, borrower can pay up to $20,000 without penalty per year',
  },
})

// For Flat type
export const getFlatPenaltyInputs = (): Record<string, InputType> => ({
  flatPercentage: {
    inputType: 'text',
    type: 'number',
    title: 'Penalty Fee (%)',
    required: true,
  },
})

// For Interest Based type
export const getInterestBasedInputs = (): Record<string, InputType> => ({
  interestMonths: {
    inputType: 'text',
    type: 'number',
    title: 'Months of Interest',
    required: true,
  },
})

// For Stepdown Entry
export const getStepdownEntryInputs = (entry: StepdownEntry): Record<string, InputType> => ({
  percentage: {
    inputType: 'text',
    title: 'Percent',
    value: entry.percentage,
  },
  startTerm: {
    inputType: 'text',
    type: 'number',
    title: 'Start Term',
    value: entry.startTerm,
  },
  endTerm: {
    inputType: 'text',
    type: 'number',
    title: 'End Term',
    value: entry.endTerm,
  },
})
